import React, { ComponentType, useCallback, useEffect, useMemo } from "react";
import { useSnapshot } from "valtio";
import clsx from "clsx";
import { LayerTypeControlPageProps } from "../../types";
import Feature from "src/js/stores/user/Feature";
import { logEvent } from "react-migration/lib/util/logEvent";
import { useControlTracking } from "../../ControlTrackingContext";
import { MapState } from "src/js/stores/map/store";
import { comparablesStore } from "src/js/stores/comparables";

import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";

import {
  setPriceType,
  setPriceMode,
  setDataSource,
  setFilters,
  setHmlrFilters,
  setHometrackFilters,
  setPriceSource,
} from "src/js/stores/comparables/actions";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { PriceMode } from "react-migration/domains/comparables/components/Visualisations/PriceMode";
import { FiltersWithClear } from "react-migration/domains/comparables/components/Filters/FiltersWithClear";
import { PriceType } from "react-migration/domains/comparables/components/Visualisations/PriceType";
import { CalculatorButton } from "react-migration/domains/comparables/components/Calculator/CalculatorButton";
import { ComparablesDataSourceSwitch } from "react-migration/layouts/comparables/ComparablesDataSourceSwitch";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { HometrackUpgradeCTA } from "./HometrackUpgradeCTA";

export const ComparablesControlPage: ComponentType<LayerTypeControlPageProps> = ({ disabled }) => {
  const { debouncedTrackControlChange, trackControlChange } = useControlTracking();
  const { hmlrFilters, hometrackFilters, filters, priceMode, priceType, dataSource, priceSource } =
    useSnapshot(comparablesStore);

  // Determine which filter state to use based on current data source
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const usAccess = hasFeature(Feature.usAccess);
  const useHometrack = hometrackv2 && !usAccess;

  const currentFilters = useMemo(() => {
    if (!useHometrack) {
      return filters;
    }
    return dataSource === "hmlr" ? hmlrFilters : hometrackFilters;
  }, [dataSource, hmlrFilters, hometrackFilters, useHometrack, filters]);

  const unitPreference = getMeasurementSystemPreference();
  const openCalc = useCallback(() => {
    logEvent("Comparables Calculator Open", {
      priceMode,
      priceType: priceType === 0 ? "full" : "ppa",
    });
    updateMapSettings("state", MapState.COMPARABLES_CALCULATOR);
  }, [priceMode, priceType]);

  const zooplaFullAccess = hasFeature(Feature.zooplaFullAccess);
  const showUpgradeCta = dataSource === "hometrack" && !zooplaFullAccess;

  const toggleDataSource = useCallback(() => {
    debouncedTrackControlChange({
      filterName: "dataSource",
      filterState: dataSource === "hmlr" ? "hometrack" : "hmlr",
    });

    setDataSource(dataSource === "hmlr" ? "hometrack" : "hmlr");
  }, [dataSource, debouncedTrackControlChange]);

  /**
   * Hack to ensure users off the beta don't get stuck within the hometrack data
   * source
   */
  useEffect(() => {
    if (!useHometrack && dataSource === "hometrack") {
      setDataSource("hmlr");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHometrack]);

  return (
    <div className={clsx("flex flex-col gap-y-2", { "opacity-50": disabled })}>
      {useHometrack ? (
        <ComparablesDataSourceSwitch dataSource={dataSource} onDataSourceChange={toggleDataSource} />
      ) : null}
      {showUpgradeCta ? (
        <HometrackUpgradeCTA />
      ) : (
        <>
          <div className="flex flex-col px-3 py-2 gap-2 pb-4 border-b border-neutral-200">
            {hasFeature(Feature.comparablesPpSqftMode) ? (
              <>
                <PriceMode
                  priceType={priceType}
                  priceMode={priceMode}
                  setPriceMode={setPriceMode}
                  useZooplaOptions={useHometrack ? dataSource === "hometrack" : zooplaFullAccess}
                  // Kinda hacky way to display the list in the old fashion
                  // TODO: Remove this when hometrackv2 is the default
                  showAllOptions={!useHometrack}
                  eventTracking={debouncedTrackControlChange}
                  priceSource={priceSource}
                  setPriceSource={setPriceSource}
                />
              </>
            ) : null}

            {hasFeature(Feature.comparablesPpSqftMode) ? (
              <PriceType
                unitPreference={unitPreference}
                priceType={priceType}
                setPriceType={setPriceType}
                eventTracking={trackControlChange}
              />
            ) : null}
          </div>

          <div className="relative duration-300 transition-colors pt-2 pb-4 border-b border-neutral-200">
            <FiltersWithClear
              filters={currentFilters}
              setFilters={setFilters}
              setHmlrFilters={setHmlrFilters}
              setHometrackFilters={setHometrackFilters}
              unitPreference={unitPreference}
              priceMode={priceMode}
              dataSource={dataSource}
              eventTracking={debouncedTrackControlChange}
            />
          </div>

          <div className="py-2">
            <CalculatorButton openCalculator={openCalc} hasLicense={hasFeature(Feature.comparablesCalculator)} />
          </div>
        </>
      )}
    </div>
  );
};
